import type { HTMLAttributes } from "react";

import { cn } from "../lib/cn";

/**
 * The card's own fill carries its urgency now — there is no accent stripe down the
 * left edge any more. A withdrawal card is red all over, which is legible from further
 * away than a 4px rule and does not need the rest of the card read to be understood.
 */
const TONES = {
  default: "bg-surface-container-lowest",
  /** The lead accent as a large fill. Text inside must be `on-secondary-container`. */
  accent: "bg-secondary-container",
  /** Something is wrong: a failed round, a live withdrawal. */
  alarm: "bg-error-container",
  /** The faintest terracotta: a failed round, which is over but not an alarm. */
  warm: "bg-secondary-faint",
  /** Recessed rather than raised — a panel inside the page, not on top of it. */
  recessed: "bg-surface-container",
} as const;

export type CardTone = keyof typeof TONES;

/**
 * `panel` is a card that owns its section — a hero, a form step, a record's
 * identity. `row` is one of several in a list, and is a step tighter so a column
 * of them does not read as a stack of panels.
 */
const RADII = {
  panel: "rounded-lg",
  row: "rounded-card",
} as const;

export type CardShape = keyof typeof RADII;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  shape?: CardShape;
  /** Drops the shadow, for a card nested inside another surface. */
  flat?: boolean;
}

/**
 * The primary vehicle for a bird profile. Borderless and over-rounded: depth comes
 * from one soft green-tinted shadow against the sage page, which is enough separation
 * without an outline drawing a second edge around every record.
 */
export function Card({
  className,
  tone = "default",
  shape = "panel",
  flat = false,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(RADII[shape], TONES[tone], flat ? "" : "shadow-card", className)}
      {...props}
    />
  );
}

/** Top-aligned header. On a bird card this is where the ring number goes. */
export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-hair p-lg pb-0", className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-panel-title text-on-surface", className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-detail text-on-surface-variant", className)} {...props} />;
}

/** Secondary section — the quick stats row: species, age, sex. */
export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-lg", className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center gap-sm p-lg pt-0", className)} {...props} />;
}

/**
 * A recessed strip inside a card — the boxed Sex / Age / Cage facts, the Day / Eggs /
 * Hatch row on a pair. Tinted down rather than outlined, so a card can hold a group of
 * related figures without a second border inside it.
 */
export function CardInset({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("rounded-inset bg-surface-container p-md", className)} {...props} />
  );
}
